import * as React from "react";

export interface DialogDragOffset {
  x: number;
  y: number;
}

interface DragRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

interface DragViewport {
  width: number;
  height: number;
}

/** Gap kept between a dragged dialog and the viewport edges. */
export const DIALOG_DRAG_MARGIN = 8;

/**
 * Dialogs that cover this much of the viewport are not draggable: there is
 * nowhere useful to move them, and dragging one only repaints a near-full-screen
 * surface on every frame.
 */
export const DIALOG_MAX_DRAGGABLE_FRACTION = 0.75;

/** Pointer/keyboard elements inside a drag surface that must not start a drag. */
const INTERACTIVE_SELECTOR =
  'button, a, input, textarea, select, label, [role="button"], [role="tab"], [role="checkbox"], [contenteditable="true"], [data-dialog-no-drag]';

function currentViewport(): DragViewport {
  return { width: window.innerWidth, height: window.innerHeight };
}

/**
 * Clamp one axis of a movement so the dialog stays on screen. When the dialog
 * is larger than the viewport the bounds invert, which lets the user pan across
 * it rather than pinning it in place.
 */
function clampAxis(
  start: number,
  move: number,
  size: number,
  viewportSize: number,
  margin: number
): number {
  const max = viewportSize - margin - size;
  const lower = Math.min(margin, max);
  const upper = Math.max(margin, max);
  return Math.min(Math.max(start + move, lower), upper) - start;
}

/** Clamp a proposed movement so the dialog stays on screen. */
export function clampDialogDelta(
  rect: DragRect,
  delta: DialogDragOffset,
  viewport: DragViewport,
  margin = DIALOG_DRAG_MARGIN
): DialogDragOffset {
  return {
    x: clampAxis(rect.left, delta.x, rect.width, viewport.width, margin),
    y: clampAxis(rect.top, delta.y, rect.height, viewport.height, margin),
  };
}

/** A dialog is draggable only while it leaves a useful amount of page visible. */
export function isDialogDraggableSize(
  rect: { width: number; height: number },
  viewport: DragViewport,
  maxFraction = DIALOG_MAX_DRAGGABLE_FRACTION
): boolean {
  if (rect.width === 0 || rect.height === 0) return true;
  return (
    rect.width <= viewport.width * maxFraction &&
    rect.height <= viewport.height * maxFraction
  );
}

/**
 * Dialogs declare `transition-none`, so position writes land immediately. A
 * consumer className can still re-enable transitions, and CSS defaults
 * `transition-property` to `all`, so the dialog would then ease toward each new
 * position and be cancelled by the next write - visibly trailing the cursor.
 * Positions are therefore always written with transitions suppressed.
 */
function suppressTransitions(element: HTMLElement) {
  element.style.transition = "none";
}

function restoreTransitions(element: HTMLElement) {
  // Read back first so the new position is committed at its final value
  // instead of animating from the old one once transitions return.
  void element.offsetHeight;
  element.style.removeProperty("transition");
}

/**
 * Write the resting position of the dialog. `left`/`top` compose with the
 * centering utilities and, unlike a transform, are not overridden by the
 * open/close zoom animation.
 */
function applyRestingOffset(element: HTMLElement, offset: DialogDragOffset) {
  if (offset.x === 0 && offset.y === 0) {
    element.style.removeProperty("left");
    element.style.removeProperty("top");
    return;
  }
  element.style.left = `calc(50% + ${offset.x}px)`;
  element.style.top = `calc(50% + ${offset.y}px)`;
}

interface ActiveDrag {
  pointerId: number;
  /** Pointer position where the drag started. */
  startX: number;
  startY: number;
  /** Latest pointer position, clamped into a transform once per frame. */
  pointerX: number;
  pointerY: number;
  /** Dialog bounds and viewport at drag start; neither changes mid-drag. */
  rect: DragRect;
  viewport: DragViewport;
  frame: number;
  detach: () => void;
}

export interface DialogDragHandleProps {
  onPointerDown: (event: React.PointerEvent<HTMLElement>) => void;
  onKeyDown: (event: React.KeyboardEvent<HTMLElement>) => void;
  onDoubleClick: (event: React.MouseEvent<HTMLElement>) => void;
}

export interface DraggableDialog {
  /** False when dragging is off, or the dialog is too large to be worth moving. */
  canDrag: boolean;
  /** Attach to the positioned dialog element so its bounds can be measured. */
  contentRef: React.RefCallback<HTMLElement>;
  handleProps: DialogDragHandleProps;
  /** Return the dialog to its centered position. */
  reset: () => void;
}

/**
 * Makes a centered, fixed-position dialog draggable so users can uncover the
 * content behind it.
 *
 * The drag never touches React state: each frame writes a `translate3d`
 * transform straight to the DOM node, so the dialog tracks the cursor without
 * re-rendering its (often very large) subtree. The transform is folded into the
 * resting `left`/`top` when the drag ends, which keeps the close animation from
 * overriding the position.
 */
export function useDraggableDialog(enabled = true): DraggableDialog {
  const [element, setElement] = React.useState<HTMLElement | null>(null);
  const [fitsViewport, setFitsViewport] = React.useState(true);
  const offsetRef = React.useRef<DialogDragOffset>({ x: 0, y: 0 });
  const dragRef = React.useRef<ActiveDrag | null>(null);

  const contentRef = React.useCallback((node: HTMLElement | null) => {
    setElement(node);
    if (node) applyRestingOffset(node, offsetRef.current);
  }, []);

  /** Fold a movement into the resting `left`/`top` and drop any transform. */
  const commitOffset = React.useCallback(
    (target: HTMLElement, offset: DialogDragOffset) => {
      offsetRef.current = offset;
      suppressTransitions(target);
      target.style.removeProperty("transform");
      applyRestingOffset(target, offset);
      restoreTransitions(target);
    },
    []
  );

  const canDrag = enabled && fitsViewport;

  /** Move by a delta, clamped to the viewport, and commit it. */
  const moveBy = React.useCallback(
    (x: number, y: number) => {
      if (!canDrag || !element) return;
      const delta = clampDialogDelta(
        element.getBoundingClientRect(),
        { x, y },
        currentViewport()
      );
      if (delta.x === 0 && delta.y === 0) return;
      commitOffset(element, {
        x: offsetRef.current.x + delta.x,
        y: offsetRef.current.y + delta.y,
      });
    },
    [canDrag, commitOffset, element]
  );

  const reset = React.useCallback(() => {
    if (element) commitOffset(element, { x: 0, y: 0 });
  }, [commitOffset, element]);

  const endDrag = React.useCallback(() => {
    const drag = dragRef.current;
    dragRef.current = null;
    if (!drag || !element) return;

    drag.detach();
    if (drag.frame) cancelAnimationFrame(drag.frame);
    element.removeAttribute("data-dragging");
    element.style.removeProperty("will-change");
    try {
      if (element.hasPointerCapture?.(drag.pointerId)) {
        element.releasePointerCapture(drag.pointerId);
      }
    } catch {
      // The pointer is already gone; nothing to release.
    }

    // Clamped from the last pointer position, which may be newer than the
    // frame that drew.
    const delta = clampDialogDelta(
      drag.rect,
      { x: drag.pointerX - drag.startX, y: drag.pointerY - drag.startY },
      drag.viewport
    );
    commitOffset(element, {
      x: offsetRef.current.x + delta.x,
      y: offsetRef.current.y + delta.y,
    });
  }, [commitOffset, element]);

  const onPointerDown = React.useCallback(
    (event: React.PointerEvent<HTMLElement>) => {
      if (!canDrag || !element || event.button !== 0 || dragRef.current) return;

      // Let controls inside the drag surface (buttons, inputs, the close
      // button) keep their own pointer behaviour.
      const interactive = (event.target as HTMLElement | null)?.closest(
        INTERACTIVE_SELECTOR
      );
      if (interactive && interactive !== event.currentTarget) return;

      const rect = element.getBoundingClientRect();
      const viewport = currentViewport();
      if (!isDialogDraggableSize(rect, viewport)) return;

      // Stops the drag from selecting the dialog title text.
      event.preventDefault();

      const handleMove = (moveEvent: PointerEvent) => {
        const drag = dragRef.current;
        if (!drag || moveEvent.pointerId !== drag.pointerId) return;
        drag.pointerX = moveEvent.clientX;
        drag.pointerY = moveEvent.clientY;

        // One write per frame, straight to the node. Pointer events can outpace
        // the display (and pile up when the main thread is busy), so extra
        // moves only record the target and the frame draws the latest position.
        // Both the clamp and the write skip React and layout entirely.
        if (drag.frame) return;
        drag.frame = requestAnimationFrame(() => {
          drag.frame = 0;
          if (dragRef.current !== drag) return;
          const delta = clampDialogDelta(
            drag.rect,
            { x: drag.pointerX - drag.startX, y: drag.pointerY - drag.startY },
            drag.viewport
          );
          element.style.transform = `translate3d(${delta.x}px, ${delta.y}px, 0)`;
        });
      };
      const stop = () => endDrag();

      // Listeners live only for the duration of the drag, so the dialogs a user
      // never drags cost nothing on pointer moves elsewhere in the page.
      window.addEventListener("pointermove", handleMove, { passive: true });
      window.addEventListener("pointerup", stop);
      window.addEventListener("pointercancel", stop);

      dragRef.current = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        pointerX: event.clientX,
        pointerY: event.clientY,
        rect,
        viewport,
        frame: 0,
        detach: () => {
          window.removeEventListener("pointermove", handleMove);
          window.removeEventListener("pointerup", stop);
          window.removeEventListener("pointercancel", stop);
        },
      };

      element.setAttribute("data-dragging", "");
      // Its own compositor layer for the drag, so moving the dialog does not
      // force the page behind it to raster again.
      element.style.willChange = "transform";
      // Held for the whole drag, so per-frame writes land immediately.
      suppressTransitions(element);
      try {
        // Keeps the drag alive over iframes and outside the window.
        element.setPointerCapture?.(event.pointerId);
      } catch {
        // Capture is a nicety; window listeners drive the drag either way.
      }
    },
    [canDrag, element, endDrag]
  );

  const onKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLElement>) => {
      if (!canDrag) return;
      // Keyboard movement arrives from the move handle. Controls inside the
      // drag surface (inputs, selects, tablists) keep their own arrow/Home keys.
      if (event.target !== event.currentTarget) return;
      const step = event.shiftKey ? 40 : 10;

      switch (event.key) {
        case "ArrowUp":
          event.preventDefault();
          moveBy(0, -step);
          break;
        case "ArrowDown":
          event.preventDefault();
          moveBy(0, step);
          break;
        case "ArrowLeft":
          event.preventDefault();
          moveBy(-step, 0);
          break;
        case "ArrowRight":
          event.preventDefault();
          moveBy(step, 0);
          break;
        case "Home":
          event.preventDefault();
          reset();
          break;
        default:
          break;
      }
    },
    [canDrag, moveBy, reset]
  );

  const onDoubleClick = React.useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      if (!canDrag) return;
      const interactive = (event.target as HTMLElement | null)?.closest(
        INTERACTIVE_SELECTOR
      );
      if (interactive && interactive !== event.currentTarget) return;
      reset();
    },
    [canDrag, reset]
  );

  // Keeps the drag affordance and the dialog's position honest as the dialog or
  // the window changes size. Measured before paint, so an oversized dialog
  // never shows an affordance it should not have.
  React.useLayoutEffect(() => {
    if (!enabled || !element) return;

    let frame = 0;
    const update = () => {
      const rect = element.getBoundingClientRect();
      const viewport = currentViewport();
      setFitsViewport(isDialogDraggableSize(rect, viewport));

      // Pull a dialog parked near an edge back into view.
      const correction = clampDialogDelta(rect, { x: 0, y: 0 }, viewport);
      if (correction.x !== 0 || correction.y !== 0) {
        commitOffset(element, {
          x: offsetRef.current.x + correction.x,
          y: offsetRef.current.y + correction.y,
        });
      }
    };

    // A resize event can arrive before the new layout is applied, so measuring
    // synchronously there reads a mismatched rect/viewport pair. Defer to the
    // next frame, where both agree.
    const schedule = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        update();
      });
    };

    update();

    // The observer covers the dialog's own box changing (it fires after
    // layout); the window listener covers the viewport changing around a
    // fixed-size dialog, which leaves its box untouched.
    const observer =
      typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver(schedule);
    observer?.observe(element);
    const onWindowResize = () => {
      // An in-flight drag is anchored to bounds the resize just invalidated.
      endDrag();
      schedule();
    };
    window.addEventListener("resize", onWindowResize);

    return () => {
      if (frame) cancelAnimationFrame(frame);
      observer?.disconnect();
      window.removeEventListener("resize", onWindowResize);
    };
  }, [commitOffset, element, enabled, endDrag]);

  // A dialog closed mid-drag must not leave its listeners behind.
  React.useEffect(() => () => dragRef.current?.detach(), []);

  const handleProps = React.useMemo(
    () => ({ onPointerDown, onKeyDown, onDoubleClick }),
    [onDoubleClick, onKeyDown, onPointerDown]
  );

  return { canDrag, contentRef, handleProps, reset };
}
